import {
  Coins,
  Landmark,
  ShieldCheck,
  Timer,
  ScanLine,
  BadgeCheck,
  Banknote,
  MapPin,
  Award,
} from "lucide-react";
import { SERVICES, TRUST_POINTS } from "@/lib/brand";
import { TESTIDS } from "@/constants/testIds";

const ICONS = {
  Coins,
  Landmark,
  ShieldCheck,
  Timer,
  ScanLine,
  BadgeCheck,
  Banknote,
  MapPin,
  Award,
};

export function ServicesGrid({ compact = false }) {
  return (
    <section
      data-testid={TESTIDS.servicesSection}
      id="services"
      className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      {!compact && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
              Our Services · सेवाएं
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">
              Three ways we help you unlock
              <span className="italic text-[#641C24]"> value from gold</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#4A3B3C] text-base leading-relaxed">
            Whether you own it, wear it, or have pledged it — Top Rate turns
            your gold into the best possible cash payout, transparently and
            fast.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {SERVICES.map((s, idx) => {
          const Icon = ICONS[s.icon];
          return (
            <div
              key={s.id}
              data-testid={TESTIDS.serviceCard(s.id)}
              className="bg-white border border-[#E5DCC5] p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                  {Icon && <Icon size={22} strokeWidth={1.6} />}
                </div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#4A3B3C] font-bold">
                  0{idx + 1}
                </div>
              </div>

              <h3 className="font-display text-2xl font-bold tracking-tight mt-6">
                {s.title}
              </h3>
              <div className="text-xs uppercase tracking-[0.22em] text-[#641C24] mt-1">
                {s.hindi} · {s.tagline}
              </div>

              <p className="text-sm text-[#4A3B3C] leading-relaxed mt-5">
                {s.desc}
              </p>

              <ul className="mt-6 space-y-2.5 text-sm text-[#2D181A]">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <span className="mt-2 w-1.5 h-1.5 bg-[#D4AF37] shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function TrustSection() {
  return (
    <section
      data-testid={TESTIDS.trustSection}
      className="bg-[#2D181A] text-[#FFFDF8]"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Why Top Rate · क्यों चुनें
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#FFFDF8] mt-4 leading-[1.05]">
              Other dealers try to manipulate.
              <br />
              <span className="italic text-[#D4AF37]">We don't.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#FFFDF8]/80 text-base leading-relaxed">
            No weight tricks, no purity fudging, no melting-loss surprises. Our
            entire evaluation happens in front of you.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
          {TRUST_POINTS.map((t) => {
            const Icon = ICONS[t.icon];
            return (
              <div key={t.title} className="flex items-start gap-4">
                <div className="w-11 h-11 border border-[#D4AF37]/60 flex items-center justify-center shrink-0 text-[#D4AF37]">
                  {Icon && <Icon size={20} strokeWidth={1.5} />}
                </div>
                <div>
                  <div className="font-display text-xl font-semibold">
                    {t.title}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]/80 mt-0.5">
                    {t.hindi}
                  </div>
                  <p className="text-sm text-[#FFFDF8]/75 leading-relaxed mt-2">
                    {t.body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
